import React, { useState, useMemo } from 'react';
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar, X, ChevronLeft, ChevronRight } from "lucide-react";
import { format, startOfMonth, endOfMonth, isSameMonth, isEqual, startOfDay, getYear, startOfYear, endOfYear } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from "@/lib/utils";

interface DateShortcut {
  label: string;
  getValue: () => { from: Date; to: Date };
}

interface ClosedMonthsDatePickerProps {
  value: { start: Date; end: Date } | null;
  onChange: (range: { start: Date; end: Date } | null) => void;
  closedMonths: Date[];
  dateShortcuts?: DateShortcut[];
  placeholder?: string;
  className?: string;
}

export const ClosedMonthsDatePicker: React.FC<ClosedMonthsDatePickerProps> = ({
  value,
  onChange,
  closedMonths,
  dateShortcuts = [],
  placeholder = "Selecionar período",
  className
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [startMonth, setStartMonth] = useState<Date | null>(value?.start || null);
  const [endMonth, setEndMonth] = useState<Date | null>(value?.end || null);
  const [isAnnualMode, setIsAnnualMode] = useState(false);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);

  const handleMonthSelect = (date: Date) => {
    const monthStart = startOfMonth(date);
    
    if (!startMonth || (startMonth && endMonth)) {
      // Starting new selection
      setStartMonth(monthStart);
      setEndMonth(null);
    } else if (monthStart < startMonth) {
      // Selected month is before start, make it the new start
      setEndMonth(startMonth);
      setStartMonth(monthStart);
    } else {
      // Selected month is after start, make it the end
      setEndMonth(monthStart);
    }
  };

  const handleApply = () => {
    if (startMonth && endMonth) {
      const start = startOfMonth(startMonth);
      const end = endOfMonth(endMonth);
      
      onChange({
        start,
        end
      });
    } else if (startMonth) {
      const start = startOfMonth(startMonth);
      const end = endOfMonth(startMonth);
      
      onChange({
        start,
        end
      });
    }
    setIsOpen(false);
  };

  const handleClear = () => {
    setStartMonth(null);
    setEndMonth(null);
    setSelectedYear(null);
    setIsAnnualMode(false);
    onChange(null);
    setIsOpen(false);
  };

  const formatDateRange = () => {
    if (!value) return placeholder;
    
    if (isSameMonth(value.start, value.end)) {
      // Single month
      return format(value.start, 'MMMM/yyyy', { locale: ptBR });
    }
    
    // Check if it's a full year selection
    const startYear = getYear(value.start);
    const endYear = getYear(value.end);
    if (startYear === endYear && 
        value.start.getMonth() === 0 && 
        value.end.getMonth() === 11) {
      return `Ano ${startYear}`;
    }
    
    // Month range
    return `${format(value.start, 'MMM/yyyy', { locale: ptBR })} - ${format(value.end, 'MMM/yyyy', { locale: ptBR })}`;
  };

  const isMonthInRange = (month: Date) => {
    if (!startMonth || !endMonth) return false;
    const monthStart = startOfMonth(month);
    return monthStart >= startOfMonth(startMonth) && monthStart <= startOfMonth(endMonth);
  };

  const isMonthSelected = (month: Date) => {
    if (!startMonth) return false;
    const monthStart = startOfMonth(month);
    return isSameMonth(monthStart, startOfMonth(startMonth)) || 
           (endMonth && isSameMonth(monthStart, startOfMonth(endMonth)));
  };

  // Converter e filtrar apenas valores Date válidos, excluir mês atual
  const sortedClosedMonths = useMemo(() => {
    const currentMonth = startOfMonth(new Date());
    
    const validDates = closedMonths
      .map(month => {
        if (month instanceof Date && !isNaN(month.getTime())) {
          return month;
        }
        if (typeof month === 'string') {
          const parsed = new Date(month);
          return !isNaN(parsed.getTime()) ? parsed : null;
        }
        return null;
      })
      .filter((date): date is Date => date !== null)
      .filter(month => !isSameMonth(month, currentMonth));
    
    return [...validDates].sort((a, b) => a.getTime() - b.getTime());
  }, [closedMonths]);

  // Extrair anos disponíveis dos meses
  const availableYears = useMemo(() => {
    const years = new Set<number>();
    sortedClosedMonths.forEach(month => {
      years.add(getYear(month));
    });
    return Array.from(years).sort((a, b) => b - a); // Mais recente primeiro
  }, [sortedClosedMonths]);

  // Filtrar meses pelo ano selecionado (only when not in annual mode)
  const filteredMonths = useMemo(() => {
    if (isAnnualMode) return [];
    if (selectedYear === null) {
      return sortedClosedMonths;
    }
    return sortedClosedMonths.filter(month => getYear(month) === selectedYear);
  }, [sortedClosedMonths, selectedYear, isAnnualMode]);

  const handleYearSelect = (year: number) => {
    if (isAnnualMode) {
      // In annual mode, selecting a year applies the full year range
      const yearStart = startOfYear(new Date(year, 0, 1));
      const yearEnd = endOfYear(new Date(year, 0, 1));
      onChange({
        start: yearStart,
        end: yearEnd
      });
      setIsOpen(false);
    } else {
      // In month mode, toggle year filter
      setSelectedYear(selectedYear === year ? null : year);
    }
  };

  const handleAnnualModeToggle = () => {
    setIsAnnualMode(!isAnnualMode);
    setSelectedYear(null);
    setStartMonth(null);
    setEndMonth(null);
  };

  const navigateYear = (direction: 'prev' | 'next') => {
    if (availableYears.length === 0) return;
    
    const currentIndex = selectedYear ? availableYears.indexOf(selectedYear) : -1;
    
    if (direction === 'prev') {
      // Ir para ano mais antigo (índice maior)
      const newIndex = currentIndex === -1 ? 0 : Math.min(currentIndex + 1, availableYears.length - 1);
      setSelectedYear(availableYears[newIndex]);
    } else {
      // Ir para ano mais recente (índice menor)
      const newIndex = currentIndex === -1 ? 0 : Math.max(currentIndex - 1, 0);
      setSelectedYear(availableYears[newIndex]);
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal",
            !value && "text-muted-foreground",
            className
          )}
        >
          <Calendar className="mr-2 h-4 w-4" />
          {formatDateRange()}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Card>
          <CardContent className="p-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">Selecionar período</h4>
                {value && (
                  <Button variant="ghost" size="sm" onClick={handleClear}>
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              
              {/* Botões de atalho + Anual toggle */}
              <div className="flex gap-2 pb-3 border-b flex-wrap">
                {dateShortcuts.map((shortcut) => {
                  const shortcutRange = shortcut.getValue();
                  const isSelected = !isAnnualMode && value && 
                    isEqual(startOfDay(value.start), startOfDay(shortcutRange.from)) &&
                    isEqual(startOfDay(value.end), startOfDay(shortcutRange.to));
                  
                  return (
                    <Button
                      key={shortcut.label}
                      variant={isSelected ? "default" : "outline"}
                      size="sm"
                      className="text-xs"
                      onClick={() => {
                        setIsAnnualMode(false);
                        const range = shortcut.getValue();
                        onChange({
                          start: range.from,
                          end: range.to
                        });
                        setSelectedYear(null);
                        setIsOpen(false);
                      }}
                    >
                      {shortcut.label}
                    </Button>
                  );
                })}
                <Button
                  variant={isAnnualMode ? "default" : "outline"}
                  size="sm"
                  className="text-xs"
                  onClick={handleAnnualModeToggle}
                >
                  Anual
                </Button>
              </div>
              
              {closedMonths.length === 0 ? (
                <div className="text-sm text-muted-foreground py-4 text-center">
                  Nenhum mês disponível
                </div>
              ) : isAnnualMode ? (
                /* Annual mode: show only year selector */
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground text-center">
                    Selecione um ano
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {availableYears.map((year) => (
                      <Button
                        key={year}
                        variant="outline"
                        size="sm"
                        className="h-10 text-sm font-medium hover:bg-primary hover:text-primary-foreground"
                        onClick={() => handleYearSelect(year)}
                      >
                        {year}
                      </Button>
                    ))}
                  </div>
                </div>
              ) : (
                /* Month mode: show year filter + month grid */
                <>
                  {/* Seletor de Ano para filtrar meses */}
                  <div className="flex items-center justify-between gap-2 pb-2 border-b">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigateYear('prev')}
                      disabled={selectedYear === availableYears[availableYears.length - 1]}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    
                    <div className="flex gap-1 flex-wrap justify-center">
                      {availableYears.map((year) => (
                        <Button
                          key={year}
                          variant={selectedYear === year ? "default" : "outline"}
                          size="sm"
                          className="h-8 px-3 text-xs"
                          onClick={() => handleYearSelect(year)}
                        >
                          {year}
                        </Button>
                      ))}
                    </div>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigateYear('next')}
                      disabled={selectedYear === availableYears[0]}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Grid de Meses */}
                  <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto">
                    {filteredMonths.map((month) => (
                      <Button
                        key={month.toISOString()}
                        variant={isMonthSelected(month) ? "default" : "outline"}
                        size="sm"
                        className={cn(
                          "h-auto p-2 text-xs",
                          isMonthInRange(month) && !isMonthSelected(month) && "bg-primary/10"
                        )}
                        onClick={() => handleMonthSelect(month)}
                      >
                        {format(month, selectedYear ? 'MMM' : 'MMM/yy', { locale: ptBR })}
                      </Button>
                    ))}
                  </div>
                  
                  {(startMonth || endMonth) && (
                    <div className="flex gap-2 pt-2 border-t">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setStartMonth(null);
                          setEndMonth(null);
                        }}
                        className="flex-1"
                      >
                        Limpar
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleApply}
                        className="flex-1"
                        disabled={!startMonth}
                      >
                        Aplicar
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </PopoverContent>
    </Popover>
  );
};
